import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { canPerform } from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { MetricCard } from "../components/MetricCard";
import { ActivityFeed } from "../components/ActivityFeed";
import { OperatorIntroChecklist } from "../components/OperatorIntroChecklist";
import {
  AlertListItem,
  HealthRow,
  OpsEmptyState,
  SectionHeader,
} from "../components/ops-display";
import {
  api,
  ApiError,
  type ObservabilityAlertEvent,
  type ObservabilityOpsSummary,
} from "../lib/api";
import { alertStatusDisplay } from "../lib/observability-ops";
import {
  alertLabelFa,
  alertSeverityTone,
  backupAgeTone,
  buildIntroChecklist,
  buildOverviewAttentions,
  buildOverviewHeaderActions,
  introChecklistStorageKey,
  driftTone,
  formatBackupAgeFa,
  killSwitchLabelFa,
  killSwitchTone,
  readinessLabelFa,
  readinessTone,
} from "../lib/overview-ops";
import { formatKabulDisplay } from "../components/ScheduleFields";
import { cmsStrings } from "../terminology";

const BANNER_KEY = "vital-dev-banner-dismissed";

export function OverviewPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const activityRef = useRef<HTMLDivElement | null>(null);

  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
  };
  const canReadEntitlements = canPerform({
    ...authInput,
    permission: "org.entitlements.read",
  });
  const canReadOps = canPerform({ ...authInput, permission: "audit.read" });
  const canReadFleet = canPerform({
    ...authInput,
    permission: "fleet.inventory.read",
  });
  const canEditContent = canPerform({ ...authInput, permission: "content.edit" });
  const canPublish = canPerform({ ...authInput, permission: "content.publish" });
  const canGenerateBundle = canPerform({
    ...authInput,
    permission: "portal.bundle.generate",
  });
  const canManageSites = canPerform({ ...authInput, permission: "sites.manage" });
  const canManageSettings = canPerform({
    ...authInput,
    permission: "settings.manage",
  });
  const isPlatformOps =
    user?.platformRole === "owner" || user?.platformRole === "operator";

  const [summary, setSummary] = useState<Awaited<
    ReturnType<typeof api.getDashboardSummary>
  > | null>(null);
  const [entitlements, setEntitlements] = useState<Awaited<
    ReturnType<typeof api.getCurrentEntitlements>
  > | null>(null);
  const [ops, setOps] = useState<ObservabilityOpsSummary | null>(null);
  const [opsUnavailable, setOpsUnavailable] = useState(false);
  const [alerts, setAlerts] = useState<ObservabilityAlertEvent[] | null>(null);
  const [alertsUnavailable, setAlertsUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [opsLoading, setOpsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(BANNER_KEY) === "1",
  );
  const introStorageKey = user?.id ? introChecklistStorageKey(user.id) : "";
  const [introDismissed, setIntroDismissed] = useState(
    () => (introStorageKey ? localStorage.getItem(introStorageKey) === "1" : true),
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOpsUnavailable(false);
    setAlertsUnavailable(false);
    try {
      setSummary(await api.getDashboardSummary());

      if (canReadEntitlements) {
        try {
          setEntitlements(await api.getCurrentEntitlements());
        } catch {
          setEntitlements(null);
        }
      } else {
        setEntitlements(null);
      }

      if (canReadOps) {
        setOpsLoading(true);
        try {
          setOps(await api.getObservabilitySummary());
        } catch {
          setOps(null);
          setOpsUnavailable(true);
        } finally {
          setOpsLoading(false);
        }

        if (isPlatformOps) {
          try {
            const res = await api.listObservabilityAlerts();
            setAlerts(res.items.slice(0, 5));
          } catch {
            setAlerts(null);
            setAlertsUnavailable(true);
          }
        } else {
          setAlerts(null);
        }
      } else {
        setOps(null);
        setAlerts(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [canReadEntitlements, canReadOps, isPlatformOps]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (searchParams.get("section") === "activity") {
      activityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searchParams, loading, summary]);

  const attentionCaps = useMemo(
    () => ({
      canReadEntitlements,
      canReadOps,
      canReadFleet,
      canEditContent,
      canPublish,
      canGenerateBundle,
      canSeePlatformAlerts: isPlatformOps,
    }),
    [
      canReadEntitlements,
      canReadOps,
      canReadFleet,
      canEditContent,
      canPublish,
      canGenerateBundle,
      isPlatformOps,
    ],
  );
  const planAttentions = useMemo(
    () =>
      buildOverviewAttentions({
        entitlements,
        ops: {
          killActive: ops?.gauges.killSwitchActive,
          readiness: ops?.readiness.status,
          backupAgeSeconds: ops?.gauges.backupAgeSeconds,
          fleetDriftSites: ops?.gauges.fleetDriftSites,
          alertCount: alerts?.length ?? 0,
        },
        draftCount: summary?.draftCount ?? 0,
        caps: attentionCaps,
      }),
    [alerts, attentionCaps, entitlements, ops, summary?.draftCount],
  );
  const headerActions = useMemo(
    () =>
      buildOverviewHeaderActions({
        canEditContent,
        canReadOps,
        canReadFleet,
        sponsoredAllowed: entitlements?.features.sponsored !== false,
      }),
    [canEditContent, canReadFleet, canReadOps, entitlements?.features.sponsored],
  );
  const introSteps = useMemo(
    () =>
      buildIntroChecklist({
        canEditContent,
        canManageSites,
        canReadOps,
        canManageSettings,
      }),
    [canEditContent, canManageSettings, canManageSites, canReadOps],
  );
  const killActive = Boolean(ops?.gauges.killSwitchActive);

  return (
    <>
      <PageHeader
        greeting
        title={`سلام، ${user?.displayName ?? "کاربر"}`}
        actions={
          headerActions.length > 0 ? (
            <>
              {headerActions.map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  className={`btn btn--sm ${action.kind === "primary" ? "btn--primary" : "btn--ghost"}`}
                >
                  {action.label}
                </Link>
              ))}
            </>
          ) : undefined
        }
      />

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}

      {killActive && !canReadOps && (
        <div className="tenant-banner tenant-banner--danger" role="alert">
          {cmsStrings.killSwitch.activeAttention}{" "}
          <Link to="/settings">{cmsStrings.nav.settings}</Link>
        </div>
      )}

      {planAttentions.length > 0 && (
        <section
          className="attention-strip"
          aria-label={cmsStrings.overviewUi.attentionAria}
          data-vt-overview-attention
        >
          <ul className="attention-strip__list">
            {planAttentions.map((item) => (
              <li
                key={item.id}
                className={`attention-strip__item${item.id === "kill" ? " attention-strip__item--critical" : item.tone === "danger" ? " attention-strip__item--danger" : ""}`}
              >
                <span>{item.text}</span>
                {item.href && (
                  <Link to={item.href}>{item.linkLabel ?? cmsStrings.nav.entitlements}</Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!introDismissed && introSteps.length > 0 && (
        <OperatorIntroChecklist
          steps={introSteps}
          onDismiss={() => {
            if (introStorageKey) localStorage.setItem(introStorageKey, "1");
            setIntroDismissed(true);
          }}
        />
      )}

      {import.meta.env.DEV && !bannerDismissed && (
        <div className="info-banner" role="status">
          <span>محیط توسعه — انتشار production تا تکمیل چک‌لیست عملیاتی مجاز نیست.</span>
          <button
            type="button"
            className="info-banner__dismiss"
            onClick={() => {
              localStorage.setItem(BANNER_KEY, "1");
              setBannerDismissed(true);
            }}
            aria-label={cmsStrings.overviewUi.dismissAria}
          >
            ×
          </button>
        </div>
      )}

      <div className="overview-layout overview-layout--ops" data-vt-overview>
        <div className="overview-main">
          <section aria-label={cmsStrings.overviewUi.contentStatusAria}>
            <div className="metric-strip metric-strip--4">
              <MetricCard
                label={cmsStrings.overviewUi.draftsLabel}
                value={summary?.draftCount ?? 0}
                loading={loading}
                to="/announcements"
              />
              <MetricCard
                label={cmsStrings.nav.announcements}
                value={summary?.publishedAnnouncements ?? 0}
                loading={loading}
                to="/announcements"
              />
              <MetricCard
                label={cmsStrings.nav.promotions}
                value={summary?.publishedPromotions ?? 0}
                loading={loading}
                to="/promotions"
              />
              <MetricCard
                label={cmsStrings.overviewUi.scheduledLabel}
                value={summary?.scheduledCount ?? 0}
                loading={loading}
              />
            </div>
          </section>

          {canReadOps ? (
          <section className="overview-section">
            <SectionHeader
              title={cmsStrings.overviewUi.healthTitle}
              actionLabel={cmsStrings.nav.observability}
              actionTo="/observability"
            />
              <div className="overview-list-surface">
                <div className="health-stack" role="list">
                  <div role="listitem">
                    <HealthRow
                      label={cmsStrings.overviewUi.readinessLabel}
                      value={readinessLabelFa(ops?.readiness.status)}
                      tone={readinessTone(ops?.readiness.status)}
                      to="/observability"
                      loading={opsLoading}
                      unavailable={opsUnavailable}
                    />
                  </div>
                  <div role="listitem">
                    <HealthRow
                      label={cmsStrings.overviewUi.backupAgeLabel}
                      value={formatBackupAgeFa(ops?.gauges.backupAgeSeconds)}
                      tone={backupAgeTone(ops?.gauges.backupAgeSeconds)}
                      to="/observability?tab=backup"
                      loading={opsLoading}
                      unavailable={opsUnavailable}
                    />
                  </div>
                  <div role="listitem">
                    <HealthRow
                      label={cmsStrings.nav.drift}
                      value={
                        ops?.gauges.fleetDriftSites != null
                          ? String(ops.gauges.fleetDriftSites)
                          : "—"
                      }
                      tone={driftTone(ops?.gauges.fleetDriftSites)}
                      to="/fleet?tab=drift"
                      loading={opsLoading}
                      unavailable={opsUnavailable}
                    />
                  </div>
                  <div role="listitem">
                    <HealthRow
                      label={cmsStrings.killSwitch.title}
                      value={killSwitchLabelFa(ops?.gauges.killSwitchActive)}
                      tone={killSwitchTone(ops?.gauges.killSwitchActive)}
                      to="/settings"
                      loading={opsLoading}
                      unavailable={opsUnavailable}
                    />
                  </div>
                </div>
              </div>
          </section>
          ) : (
            <OpsEmptyState title={cmsStrings.overviewUi.healthDeniedTitle} />
          )}

          {isPlatformOps && (
          <section className="overview-section">
            <SectionHeader
              title={cmsStrings.overviewUi.alertsTitle}
              actionLabel={cmsStrings.overviewUi.alertsAction}
              actionTo="/observability?tab=alerts"
            />
            {alertsUnavailable ? (
              <p className="overview-quiet-empty" role="status">
                {cmsStrings.overviewUi.alertsLoadFailedTitle}
              </p>
            ) : alerts == null || alerts.length === 0 ? (
              <p className="overview-quiet-empty" role="status">
                {cmsStrings.overviewUi.alertsEmptyTitle}
              </p>
            ) : (
              <div className="overview-list-surface">
                <ul className="alert-list">
                  {alerts.map((a) => (
                    <AlertListItem
                      key={a.id}
                      title={alertLabelFa(a.alert)}
                      severityLabel={a.severity || "info"}
                      tone={alertSeverityTone(a.severity)}
                      firedAt={formatKabulDisplay(a.firedAt)}
                      status={alertStatusDisplay(a.status).label}
                    />
                  ))}
                </ul>
              </div>
            )}
          </section>
          )}

        </div>

        <div ref={activityRef} id="activity">
          <section className="overview-section">
            <SectionHeader
              title={cmsStrings.overviewUi.activityTitle}
              actionLabel={cmsStrings.overviewUi.viewAll}
              actionTo="/audit"
            />
            <div className="overview-list-surface">
              <ActivityFeed items={summary?.recentAudit ?? []} loading={loading} />
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
